/**
 * OAuth start helper
 *
 * Single place that builds the `/api/auth/<provider>` URL, so every entry point
 * (login, signup, invitation landing pages) starts OAuth the same way.
 *
 * Why the language matters here: OAuth navigates the browser away to
 * Google/Microsoft and back to a fixed callback URL, so nothing we put in the
 * URL survives unless the server round-trips it through the OAuth `state`
 * parameter. The server reads `lang`, stores it as the new user's
 * preferredLanguage, and echoes it back on the final redirect. Without it a
 * Japanese signup produced an account with no language — every later email to
 * that user defaulted to English.
 */

import i18n from '../i18n';

export type OAuthProvider = 'google' | 'microsoft';

export type OAuthMode = 'login' | 'signup';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * Build the URL that starts an OAuth round trip.
 *
 * @param provider Identity provider to use
 * @param mode 'signup' forces the full consent screen (needed to obtain a
 *   refresh token); 'login' shows only the account picker. Omit when the entry
 *   point can be either (e.g. an invitation landing page) — the server then
 *   decides and redirects to consent if a refresh token turns out to be needed.
 */
export const buildOAuthUrl = (provider: OAuthProvider, mode?: OAuthMode): string => {
  const params = new URLSearchParams();

  if (mode) {
    params.set('mode', mode);
  }

  params.set('lang', i18n.language?.startsWith('ja') ? 'ja' : 'en');

  // Cache-buster: these are full page navigations and some browsers will
  // otherwise serve a cached 302 from a previous attempt.
  params.set('t', String(Date.now()));

  return `${API_URL}/api/auth/${provider}?${params.toString()}`;
};

/**
 * Navigate the browser into the OAuth flow.
 */
export const startOAuth = (provider: OAuthProvider, mode?: OAuthMode): void => {
  window.location.href = buildOAuthUrl(provider, mode);
};
